/**
 * Speculative boot prefetch hint. `CustomerAccountProvider` remembers which
 * customer account (the account UUID that balances and the ledger key on) the
 * signed-in user last viewed, bound to that user's OIDC `sub`. On the next
 * boot `useBootPrefetch` (run by `OnboardingRouter`, which is mounted while
 * `/self` is pending — the provider is not) prefetches that account's
 * balances and first ledger page while `/self` is still in flight, so
 * returning users stop waiting on `/self` before the dashboard fills.
 *
 * The hint only ever replays the same user's own last account: a stored `sub`
 * that differs from the current user's clears the hint and nothing is
 * prefetched. Logout clears it (`useLogout`), and leaving mock mode clears it
 * (`lib/mockMode.ts`).
 */

export const BOOT_HINT_STORAGE_KEY = "upBootHint.v1";

/**
 * How long a boot prefetch counts as fresh: the `ifOlderThan` of the prefetch
 * itself, and the window in which the dashboard's first mount trusts it
 * instead of refetching.
 */
export const BOOT_PREFETCH_MAX_AGE_SECONDS = 30;

const UUID_PATTERN =
  /^[0-9a-f]{8}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{12}$/i;

export type BootHint = {
  sub: string;
  customerAccountUuid: string;
};

/**
 * Kill switch, on unless `NEXT_PUBLIC_BOOT_PREFETCH` is exactly `"false"`.
 * Inlined at build time, so turning it off needs a redeploy.
 */
export function isBootPrefetchEnabled(
  flag: string | undefined = process.env.NEXT_PUBLIC_BOOT_PREFETCH
): boolean {
  return flag !== "false";
}

// `window.localStorage` itself throws (SecurityError) when storage is blocked
// (sandboxed iframes, some privacy settings). The hint is an optimisation, so
// blocked storage just means "no hint".
const defaultStorage = (): Storage | undefined => {
  if (typeof window === "undefined") return undefined;
  try {
    return window.localStorage;
  } catch {
    return undefined;
  }
};

const isBootHint = (value: unknown): value is BootHint => {
  if (typeof value !== "object" || value === null) return false;
  const { sub, customerAccountUuid } = value as Record<string, unknown>;
  return (
    typeof sub === "string" &&
    sub.length > 0 &&
    typeof customerAccountUuid === "string" &&
    UUID_PATTERN.test(customerAccountUuid)
  );
};

/** Removes the hint. Storage that refuses the write leaves nothing to clear. */
export function clearBootHint(storage = defaultStorage()): void {
  try {
    storage?.removeItem(BOOT_HINT_STORAGE_KEY);
  } catch (error) {
    // Optimisation only: a hint that cannot be removed is replaced or
    // rejected (sub check) on the next boot, so nothing is surfaced to the user.
    console.warn("[bootHint] Could not clear the boot prefetch hint", error);
  }
}

/**
 * The account UUID to prefetch for `sub`, or `null`. A malformed value, or one
 * stored for a different `sub`, is removed and yields `null`.
 */
export function readBootHint(
  sub: string,
  storage = defaultStorage()
): string | null {
  if (!storage || !sub) return null;
  let raw: string | null;
  try {
    raw = storage.getItem(BOOT_HINT_STORAGE_KEY);
  } catch {
    // Blocked storage: no hint, prefetch nothing.
    return null;
  }
  if (raw === null) return null;

  let parsed: unknown;
  try {
    parsed = JSON.parse(raw);
  } catch (error) {
    // Corrupt value: drop it so the next boot writes a clean one.
    console.warn("[bootHint] Dropping an unreadable boot prefetch hint", error);
    clearBootHint(storage);
    return null;
  }
  if (!isBootHint(parsed) || parsed.sub !== sub) {
    clearBootHint(storage);
    return null;
  }
  return parsed.customerAccountUuid;
}

/**
 * Stores the hint. An id that is not UUID-shaped is never stored (and drops
 * any earlier hint), so a malformed id cannot be replayed into a request URL.
 */
export function writeBootHint(
  hint: BootHint,
  storage = defaultStorage()
): void {
  if (!storage) return;
  if (!isBootHint(hint)) {
    clearBootHint(storage);
    return;
  }
  try {
    storage.setItem(
      BOOT_HINT_STORAGE_KEY,
      JSON.stringify({
        sub: hint.sub,
        customerAccountUuid: hint.customerAccountUuid,
      })
    );
  } catch (error) {
    // Quota or blocked storage: the next boot simply skips the prefetch.
    console.warn("[bootHint] Could not store the boot prefetch hint", error);
  }
}

// ---------------------------------------------------------------------------
// Per-page-load state for the dashboard's first mount
// ---------------------------------------------------------------------------

let bootPrefetchedAtMs: number | undefined;
let dashboardMountedSincePageLoad = false;

/** Records that the boot prefetch was dispatched on this page load. */
export function markBootPrefetched(nowMs: number = Date.now()): void {
  bootPrefetchedAtMs = nowMs;
}

/**
 * Whether the dashboard should skip its mount refetch and first-mount
 * invalidation: only on the first dashboard mount since page load, and only
 * while this page load's boot prefetch is still fresh. Pure, so it is safe in
 * a state initialiser that React may call twice.
 */
export function shouldSkipDashboardBootRefresh(
  nowMs: number = Date.now()
): boolean {
  return (
    !dashboardMountedSincePageLoad &&
    bootPrefetchedAtMs !== undefined &&
    nowMs - bootPrefetchedAtMs <= BOOT_PREFETCH_MAX_AGE_SECONDS * 1000
  );
}

/** Called once the dashboard has mounted; later mounts refresh as usual. */
export function markDashboardMounted(): void {
  dashboardMountedSincePageLoad = true;
}

/** Test-only: forget this page load's prefetch and dashboard mount. */
export function resetBootPrefetchStateForTests(): void {
  bootPrefetchedAtMs = undefined;
  dashboardMountedSincePageLoad = false;
}
